import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const STATUS_COLORS = { submitted: '#3b82f6', processing: '#f59e0b', active: '#10b981', rejected: '#dc2626' };
const STATUS_LABELS = { submitted: 'Submitted', processing: 'Processing', active: 'Active', rejected: 'Rejected' };

export default function NewAccountListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canManage = can('manage_tasks');

  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.search = search;
      if (sortBy) { filters.sort_by = sortBy; filters.sort_dir = sortDir; }

      const [data, statsData] = await Promise.all([
        api.getAccounts(filters),
        api.getAccountStats(),
      ]);
      setAccounts(Array.isArray(data) ? data : []);
      setStats(statsData || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [startDate, endDate, statusFilter, search, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  function handleSort(col) {
    if (sortBy !== col) { setSortBy(col); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else { setSortBy(''); setSortDir(''); }
  }
  function arrow(col) { if (sortBy !== col) return ''; return sortDir === 'asc' ? ' \u25B2' : ' \u25BC'; }

  function formatDate(d) {
    if (!d) return '';
    return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const hasFilters = startDate || endDate || statusFilter || search;

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>All Accounts</h1>
        <button className="btn btn-primary" onClick={() => navigate('/sales/accounts/new')}>+ New Account</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: stats.total || 0, color: '#0f172a' },
          { label: 'Submitted', value: stats.submitted || 0, color: '#3b82f6' },
          { label: 'Processing', value: stats.processing || 0, color: '#f59e0b' },
          { label: 'Active', value: stats.active || 0, color: '#10b981' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', borderRadius: 12, padding: '14px 16px',
            border: '1px solid #e2e8f0', textAlign: 'center',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} title="Start date" />
        <span style={{ color: '#94a3b8', fontSize: 12 }}>to</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} title="End date" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, minWidth: 140 }} />
        {hasFilters && (
          <button onClick={() => { setStartDate(''); setEndDate(''); setStatusFilter(''); setSearch(''); }}
            style={{ fontSize: 11, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
            Clear filters
          </button>
        )}
        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{accounts.length} result{accounts.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : accounts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>&#128203;</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>No accounts found</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {hasFilters ? 'Try adjusting your filters' : 'No new accounts submitted yet'}
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('property_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Property{arrow('property_name')}
                </th>
                <th>Address</th>
                <th onClick={() => handleSort('contact_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Contact{arrow('contact_name')}
                </th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Status{arrow('status')}
                </th>
                <th>Submitted By</th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Date{arrow('created_at')}
                </th>
                <th style={{ textAlign: 'center' }}>Docs</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id} onClick={() => navigate(`/sales/accounts/${a.id}`)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600 }}>{a.property_name}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>
                    {[a.address, a.city, a.state].filter(Boolean).join(', ') || '-'}
                  </td>
                  <td>{a.contact_name || '-'}</td>
                  <td>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: `${STATUS_COLORS[a.status] || '#94a3b8'}18`, color: STATUS_COLORS[a.status] || '#94a3b8',
                    }}>
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{a.submitted_by_name || '-'}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{formatDate(a.created_at)}</td>
                  <td style={{ textAlign: 'center' }}>{a.doc_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
